<script lang="ts">
  import {isValidPhoneNumber} from 'libphonenumber-js/max';
  import {incentiveTierStore} from 'Store/incentiveTierStore';
  import {TierType} from 'Types/incentiveTier/incentiveTierTypes';
  import {
    personalInfo,
    postingInvestor,
    deal,
    phoneTooltip,
    recaptcha,
    recaptchaToken,
    initialEmailValue,
    Step,
    postInvestorMultiPart,
    accessLink,
    currentStep,
    apiError,
    showRecaptchaV3,
    dealExperiments,
    hasSubmittedPromotion,
    getSocialProofingData,
    dealMakerUrl,
    jwtToken,
    dealId,
  } from 'Store/InvestorFlowStore';
  import {validateEmail} from 'Utility/validation';
  import {transitionToAuthentication} from 'Utility/investorFlow/transitionFunction';
  import {ukCode} from 'Utility/commons/countries/codes';
  import User from 'Components/InvestorFlow/icons/User.svelte';
  import Mail from 'Components/InvestorFlow/icons/Mail.svelte';
  import OrientedTooltip from 'Components/InvestorFlow/OrientedTooltip.svelte';
  import SubmitButton from 'Components/InvestorFlow/v2/Components/LeftContainer/SubmitButton.svelte';
  import PhoneInput from 'Components/Common/Form/PhoneInput.svelte';
  import Form from 'Components/Common/Form/Form.svelte';
  import PersonalInfoForm from 'Components/InvestorFlow/v2/Components/LeftContainer/PersonalInfoForm.svelte';
  import SocialProofing from 'Components/InvestorFlow/v2/Components/LeftContainer/SocialProofing.svelte';
  import SocialLogin from 'Components/SocialLogin/SocialLogin.svelte';
  import {incentivePlans} from 'Store/deals/incentivePlan';
  import {embeddedWebComponent} from 'Store/embedded/embeddedFlowStore';

  export let onSubmit: () => Promise<void> = async () => {};
  export let formContainer: HTMLElement;

  let disableSubmitButton: boolean = true;
  let displayTooltipEmailInvalid: boolean = false;
  let emailConfirmationChanged: boolean = false;
  let showFields: boolean = $deal.feature_flags.investor_conversion_social_login
    ? false
    : true;
  let hideEmailField = false;

  const requireNameFields = $deal.setting.require_name_fields;
  const separateNameFields = $deal.setting.separate_name_fields;

  $: socialLoginEnabled = $deal.feature_flags.investor_conversion_social_login
    ? $dealExperiments?.displaySocialLogin
    : false;

  $: isValidEmail = $initialEmailValue
    ? true
    : validateEmail($personalInfo.email);
  $: isValidPhone =
    !$personalInfo.phone.length ||
    isValidPhoneNumber($personalInfo.phone, 'US');
  $: isRecaptchaEmpty = Boolean($recaptchaToken) || !$recaptcha.render;
  $: isValidEmailConfirmation = $dealExperiments?.displayEmailConfirmation
    ? $personalInfo.email === $personalInfo.email_confirmation
    : true;
  $: displayErrorOnConfirmationField =
    emailConfirmationChanged && !isValidEmailConfirmation;

  $: isValidName = requireNameFields ? !!$personalInfo.name : true;

  $: isValidFirstName = requireNameFields ? !!$personalInfo.first_name : true;

  $: isValidLastName = requireNameFields ? !!$personalInfo.last_name : true;

  $: hasValidNameFields = separateNameFields
    ? isValidFirstName && isValidLastName
    : isValidName;

  $: {
    if ($personalInfo.email) {
      initialEmailValue.set(!validateEmail($personalInfo.email));
      showFields = true;
      disableSubmitButton =
        !hasValidNameFields ||
        !isValidEmailConfirmation ||
        !isRecaptchaEmpty ||
        !isValidEmail ||
        $initialEmailValue ||
        !isValidPhone ||
        $postingInvestor;
    }
  }

  const submitInvestor = async (): Promise<void> => {
    await onSubmit();

    await nextStep();
  };

  const hasFreeSharesCodes = () =>
    $incentiveTierStore.items.some(
      tier => tier.tierType === TierType.FREE_SHARES_CODES
    );

  const shouldShowFreeShareCodes = () => {
    return (
      $deal.feature_flags.enhanced_perks_free_shares_codes_enabled &&
      !$hasSubmittedPromotion &&
      hasFreeSharesCodes()
    );
  };

  const nextStep = async () => {
    disableSubmitButton = true;
    const {phone} = $personalInfo;

    if (!phone && $deal.setting.landing_page_phone_number_required) {
      isValidPhone = false;
    } else {
      await postInvestorMultiPart({
        recaptcha_token: $recaptchaToken,
        recaptcha_invisible: $showRecaptchaV3,
      });

      if (!$apiError) {
        if (!$accessLink) {
          if (shouldShowFreeShareCodes()) {
            currentStep.set(Step.INCENTIVE_TIER);
          } else {
            await incentivePlans.loadInvestor($dealId, $jwtToken);
            currentStep.set(Step.INVESTMENT_AMOUNT);
          }
        } else {
          transitionToAuthentication();
        }
      }
    }
    disableSubmitButton = false;
  };

  const onSocialLogin = () => {
    hideEmailField = true;
    submitInvestor();
  };

  const enableUkOnlyRegS = $deal?.feature_flags?.enable_uk_only_reg_s;
  const initialPhoneCountry = enableUkOnlyRegS ? ukCode : undefined;

  const touchedNameFields = {
    first_name: false,
    last_name: false,
    name: false,
  };

  const handleEmailFieldFocus = () => {
    displayTooltipEmailInvalid = true;
    showFields = true;
  };

  $: inputFields = separateNameFields
    ? [
        {
          name: 'first_name',
          placeholder: `First Name${requireNameFields ? '' : ' (optional)'}`,
          id: 'user_first_name',
          testid: 'first-name-input',
          error: touchedNameFields.first_name && !isValidFirstName,
          autocomplete: 'given-name',
        },
        {
          name: 'last_name',
          placeholder: `Last Name${requireNameFields ? '' : ' (optional)'}`,
          id: 'user_last_name',
          testid: 'last-name-input',
          error: touchedNameFields.last_name && !isValidLastName,
          autocomplete: 'family-name',
        },
      ]
    : [
        {
          name: 'name',
          placeholder: `Name${requireNameFields ? '' : ' (optional)'}`,
          id: 'user_name',
          testid: 'name-input',
          error: touchedNameFields.name && !isValidName,
          autocomplete: 'name',
        },
      ];
</script>

{#if $deal.feature_flags.investor_conversion_email_error_message}
  <PersonalInfoForm
    bind:formContainer
    {initialPhoneCountry}
    {inputFields}
    {submitInvestor}
  >
    <div slot="recaptcha">
      <slot name="recaptcha" />
    </div>
  </PersonalInfoForm>
{:else}
  <Form on:submit={submitInvestor}>
    <div data-testid="personal-info">
      {#if socialLoginEnabled}
        <div
          class="social-login-container"
          data-testid="social-login-buttons-container"
        >
          <SocialLogin
            hostURL={$dealMakerUrl?.origin}
            isEmbedded={$embeddedWebComponent}
            on:submit={onSocialLogin}
          />
          <div class="social-or" class:hidden={hideEmailField}>OR</div>
        </div>
      {:else}
        <div class="begin common">Begin your investment</div>
      {/if}
      <div
        class="form-container"
        data-testid="personal-info-input-fields"
        bind:this={formContainer}
        class:hidden={hideEmailField}
      >
        <div class="form-group-row">
          <Mail error={!isValidEmail} />
          <div class="tooltip-wrapper">
            <OrientedTooltip
              tooltipText="Invalid email"
              position="top"
              error={!isValidEmail}
              isFocused={displayTooltipEmailInvalid}
              fixedLayout
            />
          </div>
          <input
            data-testid="email-input"
            bind:value={$personalInfo.email}
            type="email"
            placeholder="Email"
            id="user_email"
            class:field-danger={!isValidEmail}
            on:focus={handleEmailFieldFocus}
            on:change={() => {
              displayTooltipEmailInvalid = false;
              initialEmailValue.set(false);
            }}
            autocomplete="email"
          />
        </div>

        {#if showFields}
          {#if $dealExperiments?.displayEmailConfirmation}
            <div class="form-group-row">
              <Mail error={displayErrorOnConfirmationField} />
              <div class="tooltip-wrapper">
                <OrientedTooltip
                  tooltipText="Emails must match"
                  position="top"
                  error={displayErrorOnConfirmationField}
                  isFocused={emailConfirmationChanged}
                  fixedLayout
                />
              </div>
              <input
                data-testid="email-confirmation-input"
                bind:value={$personalInfo.email_confirmation}
                type="email"
                placeholder="Confirm email"
                id="user_email_confirmation"
                class:field-danger={displayErrorOnConfirmationField}
                on:focus={() => (emailConfirmationChanged = false)}
                on:focusout={() => (emailConfirmationChanged = true)}
                on:copy={e => e.preventDefault()}
                on:cut={e => e.preventDefault()}
                on:paste={e => e.preventDefault()}
                autocomplete="email"
              />
            </div>
          {/if}

          {#each inputFields as { name, placeholder, id, testid, error, autocomplete }}
            <div class="form-group-row">
              <span class:icon-error={error} data-testid={`${testid}-icon`}>
                <User />
              </span>
              <input
                bind:value={$personalInfo[name]}
                data-testid={testid}
                class:field-danger={error}
                type="text"
                {placeholder}
                {id}
                {autocomplete}
                on:input={() => (touchedNameFields[name] = true)}
              />
            </div>
          {/each}

          <div class="form-group-row">
            <PhoneInput
              name="phoneNumber"
              placeholder={`Phone number ${
                $deal.setting.landing_page_phone_number_required
                  ? ''
                  : '(optional)'
              }`}
              bind:value={$personalInfo.phone}
              isValid={isValidPhone}
              isFocused={$phoneTooltip || !isValidPhone}
              newLayout
              initialCountry={initialPhoneCountry}
              hasTooltip
              class="dm-phone-input"
            />
          </div>
        {/if}

        <slot name="recaptcha" />

        <SubmitButton disabled={disableSubmitButton} />
      </div>
      <div class="common-subtitle">
        By beginning the investment process, you consent to receive
        communications via email or SMS regarding updates to this offer, and may
        unsubscribe at any time.
      </div>
    </div>
  </Form>
{/if}

{#if $dealExperiments?.displaySocialProofing}
  {#await getSocialProofingData() then}
    <SocialProofing />
  {/await}
{/if}

<style lang="postcss">
  .form-group-row {
    & > :global(*) {
      color: var(--dm-text-primary-color, var(--palettes-neutral-800, #262626));
    }
    & :global(.dm-phone-input) {
      color: var(--dm-text-primary-color, inherit) !important;
      background-color: var(
        --dm-bg-primary-color,
        var(--palettes-neutral-0, #ffffff)
      ) !important;
      border: solid 0.1rem
        var(--dm-input-border-color, var(--palettes-neutral-150, #d9d9d9)) !important;
    }
    & :global(.iti__country-list) {
      color: var(--dm-text-primary-color, inherit);
      background-color: var(--dm-bg-primary-color, #ffffff);
    }
  }

  .form-container {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  .form-group-row {
    display: flex;
    position: relative;
    flex-direction: column;
    position: relative;
  }

  .icon-error {
    color: var(--dm-text-error-color, var(--palettes-error-500, red));
  }

  input {
    border: solid 0.1rem
      var(--dm-input-border-color, var(--palettes-neutral-150, #d9d9d9));
    border-radius: 0.25rem;
    height: 2.5rem;
    padding-left: 1.8rem;
    padding-right: 2rem;
    background-color: var(
      --dm-bg-primary-color,
      var(--palettes-neutral-0, #ffffff)
    );
  }

  input:focus {
    outline: none;
  }

  input:placeholder-shown {
    padding-left: 1.8rem;
    color: var(--dm-text-secondary-color, var(--palettes-neutral-425, #8c8c8c));
  }

  .field-danger {
    border-color: var(--dm-text-error-color, var(--palettes-error-500, red));
  }

  .tooltip-wrapper {
    position: relative;
    bottom: 0.39rem;
  }

  .common-subtitle {
    font-weight: 400;
    font-size: 0.75rem;
    line-height: 1.125rem;
    color: var(--dm-text-secondary-color, var(--palettes-neutral-575, #667085));
    text-align: center;
    margin-top: 1.2rem;
  }

  .begin {
    font-weight: 600;
    font-size: 1.4rem;
    line-height: 2rem;
    color: var(--dm-text-primary-color, var(--palettes-neutral-950, #101828));
    margin-bottom: 1.5rem;
  }

  .social-or {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
  }

  .social-login-container {
    padding-top: 1.5rem;
  }

  .hidden {
    display: none;
  }
</style>
